import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { keyframes, styled } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import FeeScheduleTag from "_shared/fee-schedules/tags/fee-schedule-tag";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/_shared/masked-fields";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import {
  FeeScheduleTemplateOrderField,
  FeeScheduleTemplateOrderParameters,
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import {
  FeeScheduleTemplateLoadIssue,
  getErrorLoadingFeeScheduleTemplatesByQuery,
  getFeeScheduleTemplateQueryByInterface,
  getFeeScheduleTemplatesByQuery,
  getIsLoadingFeeScheduleTemplatesByQuery,
  isFeeScheduleTemplateLoadIssue,
  populateFeeScheduleTemplatesByQuery,
} from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import {
  FeeScheduleTagChip,
  TemplateList,
  TemplateLoader,
} from "work/fee-schedule-templates/_views/_shared/embedded-containers";
import FeeScheduleTemplatesTable from "work/fee-schedule-templates/_views/tables/fee-schedule-templates-table";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

const pulseBorder = keyframes`
    0%, 100% {
        box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
        border-color: rgb(21, 101, 192);
        box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
    }
`;
const ListContainer = styled(Grid)(() => ({
  height: "100%",
  minHeight: "25rem",
}));
const TemplateContainer = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    width: "100%",
  },
}));
const CategorySelection = styled(FeeScheduleTemplatesTable)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  height: "100%",
  flex: 1,
  marginLeft: theme.spacing(0),
}));
const IssueContainer = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
    margin: active ? theme.spacing(1) : "initial",
    marginLeft: theme.spacing(active ? 0.5 : 0),
    marginBottom: theme.spacing(1),
    padding: active ? theme.spacing(1) : "initial",
    paddingLeft: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  }),
);

type SelectFeeScheduleProps = {
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  commentsToolTip: string;
  isSaved: boolean;
  commentThreads?: CommentThread[];
  disableEditing?: boolean;
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
  onProposalBuilderUpdated: (updatedProposalBuilder: ProposalBuilder) => void;
};

export default function FeeScheduleTab(props: Readonly<SelectFeeScheduleProps>) {
  const INTERFACE_NAME = "DraftProposalFeeScheduleTab";

  const {
    activeReviewField,
    proposalBuilder,
    commentThreads,
    commentsToolTip,
    isSaved,
    disableEditing,
    onCommentsClicked,
    onProposalBuilderUpdated,
  } = props;

  const [expandedTemplates, setExpandedTemplates] = useState<Set<FeeScheduleTemplate>>(new Set());
  const selectedCategories = Masked.isPresentNotMasked(proposalBuilder?.currentSpec?.workAgreement?.feeSchedule)
    ? proposalBuilder.currentSpec.workAgreement.feeSchedule
    : [];

  const issues = React.useMemo(
    () =>
      props.issues?.entries.filter((issue) =>
        [ProposalField.FeeSchedule, ProposalField.FeeScheduleCategory(issue.field.id ?? Guid.EMPTY)].includes(
          issue.field,
        ),
      ),
    [props.issues?.entries],
  );

  const isLargeDownDisplay = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const paginationParameters = new PaginationParameters();
  const filterParameters = new FeeScheduleTemplateFilterParameters({ entityId: session.user!.legalEntityId });
  const orderParameters = new FeeScheduleTemplateOrderParameters({
    orderBy: FeeScheduleTemplateOrderField.Name,
    orderDir: OrderDirection.Descending,
  });
  const defaultTemplateQuery: FeeScheduleTemplateQuery = new FeeScheduleTemplateQuery({
    filterParameters,
    orderParameters,
    paginationParameters: paginationParameters ?? PaginationParameters.create({ pageIndex: 0, pageSize: 100 }),
  });

  const templateQuery = useSelector(getFeeScheduleTemplateQueryByInterface(INTERFACE_NAME)) ?? defaultTemplateQuery;
  const templateFeeSchedules = useSelector(getFeeScheduleTemplatesByQuery(templateQuery));
  const isLoading = useSelector(getIsLoadingFeeScheduleTemplatesByQuery(templateQuery));
  const errorLoading = useSelector(getErrorLoadingFeeScheduleTemplatesByQuery(templateQuery));

  useEffect(() => {
    if (templateFeeSchedules === undefined && !isLoading && !errorLoading) {
      dispatch(populateFeeScheduleTemplatesByQuery({ query: templateQuery, session, interfaceName: INTERFACE_NAME }));
    }
  }, [dispatch, errorLoading, isLoading, session, templateFeeSchedules, templateQuery]);

  function handleTemplateExpanded(template: FeeScheduleTemplate) {
    let updatedExpandedTemplates = new Set(expandedTemplates);
    if (updatedExpandedTemplates.has(template)) {
      updatedExpandedTemplates.delete(template);
    } else {
      updatedExpandedTemplates.add(template);
    }
    setExpandedTemplates(updatedExpandedTemplates);
  }

  function handleTemplateSelectionToggled(template: FeeScheduleTemplate) {
    let updatedSelectedTemplateIds = session.context?.viewingAsVendor
      ? [...(proposalBuilder.currentSpec.workAgreement.vendorFeeScheduleTemplateIds ?? [])]
      : [...(proposalBuilder.currentSpec.workAgreement.clientFeeScheduleTemplateIds ?? [])];
    try {
      if (!template.id) throw new Error("No template selected");

      let updatedProposalBuilder = proposalBuilder;
      if (updatedSelectedTemplateIds.some((id) => id.isEqualTo(template.id))) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter((id) => !id.isEqualTo(template.id));
      } else {
        updatedSelectedTemplateIds.push(template.id);
        const categories = _.uniqWith(
          [...selectedCategories, ...template.categories],
          (a, b) => a.name?.isEqualTo(b.name) ?? false,
        );
        updatedProposalBuilder = proposalBuilder.setFeeSchedule(categories);
      }
      updatedProposalBuilder = session.context?.viewingAsVendor
        ? updatedProposalBuilder.setVendorFeeScheduleTemplateIds(updatedSelectedTemplateIds)
        : updatedProposalBuilder.setClientFeeScheduleTemplateIds(updatedSelectedTemplateIds);

      onProposalBuilderUpdated(updatedProposalBuilder);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to apply template. Please try again", {
        variant: "error",
      });
    }
  }

  function handleTemplateCategoryToggled(
    event: React.MouseEvent,
    selectedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ) {
    event.stopPropagation();
    const selectedTemplateIds = session.context?.viewingAsVendor
      ? proposalBuilder.currentSpec.workAgreement.vendorFeeScheduleTemplateIds
      : proposalBuilder.currentSpec.workAgreement.clientFeeScheduleTemplateIds;

    let updatedTemplateIds = session.context?.viewingAsVendor
      ? [...(proposalBuilder.currentSpec.workAgreement.vendorFeeScheduleTemplateIds ?? [])]
      : [...(proposalBuilder.currentSpec.workAgreement.clientFeeScheduleTemplateIds ?? [])];
    if (selectedTemplateIds?.some((id) => id.isEqualTo(selectedCategory.id))) {
      updatedTemplateIds = updatedTemplateIds.filter((id) => !id.isEqualTo(selectedCategory.id));
    } else {
      updatedTemplateIds.push(selectedCategory.id);
    }
    let updatedProposalBuilder = session.context?.viewingAsVendor
      ? proposalBuilder.setVendorFeeScheduleTemplateIds(updatedTemplateIds)
      : proposalBuilder.setClientFeeScheduleTemplateIds(updatedTemplateIds);

    let updatedCategories = [...selectedCategories];
    if (selectedCategories.some((category) => category.id.isEqualTo(selectedCategory.id))) {
      updatedCategories = updatedCategories.filter((category) => !category.id.isEqualTo(selectedCategory.id));
    } else {
      updatedCategories.push(selectedCategory);
    }
    updatedProposalBuilder = updatedProposalBuilder.setFeeSchedule(updatedCategories);

    onProposalBuilderUpdated(updatedProposalBuilder);
  }

  function handleCategoryAdded(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    if (selectedCategories.some((c) => c.isEqualTo(category))) {
      enqueueSnackbar("Fee category is already in the fee schedule", { variant: "warning" });
      return;
    }

    const updatedCategories = [
      ...selectedCategories,
      category instanceof FeeScheduleCategoryMaskedCurrency
        ? new FeeScheduleCategoryMaskedCurrency({
            id: Guid.generate(),
            name: category.name,
            description: category.description ?? null,
            billingCode: category.billingCode ?? null,
          })
        : new FeeScheduleCategory({
            name: category.name,
            description: category.description ?? null,
            fee: category.fee,
            billingCode: category.billingCode ?? null,
            id: Guid.generate(),
          }),
    ];

    onProposalBuilderUpdated(proposalBuilder.setFeeSchedule(updatedCategories));
  }

  function handleCategoryRemoved(categoryName: FeeScheduleCategoryName) {
    const updatedCategories = selectedCategories.filter((category) => !category.name?.isEqualTo(categoryName));

    onProposalBuilderUpdated(proposalBuilder.setFeeSchedule(updatedCategories));
  }

  function handleCategoryUpdated(
    originalName: FeeScheduleCategoryName | null,
    updatedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ) {
    if (originalName === null) return;

    if (selectedCategories.some((c) => c.isEqualTo(updatedCategory))) {
      enqueueSnackbar("Fee category is already in the fee schedule", { variant: "warning" });
      return;
    }

    const updatedCategories = selectedCategories.map((category) => {
      if (category.name?.isEqualTo(originalName)) return updatedCategory;
      return category;
    });

    onProposalBuilderUpdated(proposalBuilder.setFeeSchedule(updatedCategories));
  }

  function renderTemplates() {
    const selectedTemplateIds = session.context?.viewingAsVendor
      ? proposalBuilder.currentSpec.workAgreement?.vendorFeeScheduleTemplateIds
      : proposalBuilder.currentSpec.workAgreement?.clientFeeScheduleTemplateIds;
    return (
      <TemplateList dense subheader={<ListSubheader>Templates</ListSubheader>}>
        {templateFeeSchedules?.totalElements === 0 && (
          <>
            {isLoading ? (
              <TemplateLoader size={32} thickness={4} disableShrink />
            ) : (
              <ListItem key="noTemplates">
                <ListItemText>No Templates</ListItemText>
              </ListItem>
            )}
          </>
        )}
        {templateFeeSchedules?.data.map((template: FeeScheduleTemplate | FeeScheduleTemplateLoadIssue) => {
          const templateIsSelected = selectedTemplateIds?.some((id) => id.isEqualTo(template.id));
          if (isFeeScheduleTemplateLoadIssue(template)) {
            return (
              <ListItem key={template.id?.value}>
                <ListItemText primary={`Error loading template.`} />
              </ListItem>
            );
          }
          return (
            <div key={template.id?.value}>
              <ListItem
                secondaryAction={
                  <Tooltip title={`${expandedTemplates.has(template) ? "Hide" : "Show"} Categories`}>
                    <span>
                      <IconButton edge="end" onClick={() => handleTemplateExpanded(template)} size="medium">
                        {expandedTemplates.has(template) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemIcon>
                  <Tooltip title={templateIsSelected ? "Deselect Template" : "Select Template"}>
                    <span>
                      <Checkbox
                        edge="start"
                        color="primary"
                        checked={templateIsSelected}
                        indeterminate={!templateIsSelected && template.isUsing(selectedCategories)}
                        tabIndex={-1}
                        disableRipple
                        onChange={() => handleTemplateSelectionToggled(template)}
                      />
                    </span>
                  </Tooltip>
                </ListItemIcon>
                <ListItemText
                  primary={template.name}
                  secondary={template.tags.map((tag: FeeScheduleTag) => (
                    <FeeScheduleTagChip key={tag.value} size="small" label={tag.value} />
                  ))}
                />
              </ListItem>
              <Collapse in={expandedTemplates.has(template)} timeout="auto" style={{ backgroundColor: "#FAFAFA" }}>
                <Divider />
                <List disablePadding dense>
                  {renderTemplateCategories(template)}
                </List>
                <Divider />
              </Collapse>
            </div>
          );
        })}
      </TemplateList>
    );
  }

  function renderTemplateCategories(template: FeeScheduleTemplate) {
    return template.categories.map((category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => {
      const categoryIsSelected = selectedCategories.some((selectedCategory) =>
        selectedCategory.id.isEqualTo(category.id),
      );
      return (
        <ListItem
          dense
          key={category.name.value}
          onClick={(event) => handleTemplateCategoryToggled(event, category)}
          secondaryAction={
            <Checkbox
              edge="end"
              color="primary"
              size="small"
              checked={categoryIsSelected}
              tabIndex={-1}
              disableRipple
              onClick={(event) => handleTemplateCategoryToggled(event, category)}
            />
          }
        >
          <ListItemText
            primary={category.name.value}
            secondary={
              <>
                {category.description?.value}
                <br />
                {category instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : category.feeString}
              </>
            }
          />
        </ListItem>
      );
    });
  }

  if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement?.feeSchedule)) {
    return "You do not have permission to view the fee schedule for this proposal.";
  }

  return (
    <ListContainer container direction="row">
      {!disableEditing && (
        <>
          <TemplateContainer>
            {renderTemplates()}
            {isLargeDownDisplay && <Divider />}
          </TemplateContainer>
          {!isLargeDownDisplay && (
            <Grid>
              <Divider orientation="vertical" />
            </Grid>
          )}
        </>
      )}
      <Grid size="grow" sx={{ mt: 1 }}>
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer active={issues?.some((issue) => issue.field.isEqualTo(activeReviewField)) ?? false}>
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.cacheKey}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <CategorySelection
          categories={selectedCategories}
          issues={issues}
          hideFromTemplateButton={true}
          commentsToolTip={commentsToolTip}
          commentThreads={commentThreads}
          disableComments={!isSaved}
          disableEditing={disableEditing}
          activeReviewField={activeReviewField}
          onCommentsClicked={onCommentsClicked}
          onCategoryAdded={handleCategoryAdded}
          onCategoryRemoved={handleCategoryRemoved}
          onCategoryUpdated={handleCategoryUpdated}
        />
      </Grid>
    </ListContainer>
  );
}
